import React, { FC } from 'react';
import UserPermissionGate from 'components/UserPermissionGate';
import { ButtonProps, Icon, IconButtonProps, Tooltip } from '@kontentino/ui';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons/faCircleExclamation';
import Link from 'components/shared/Link';
import { ActionIconButton } from 'app/components/actions/ActionIconButton';
import { useActions } from 'app/hooks/useActions';
import { resolveLabel } from 'actions';
import { ActionButton } from 'app/components/actions/ActionButton';
import QuickActionsMoreDropdown from './QuickActionsMoreDropdown';
import { PostDetail } from 'types/PostDetail';
import { isLocalPostLocked } from 'app/modules/posts/utils/globalPosts';
import { useTranslation } from 'react-i18next';

type Props = {
  data: PostDetail;
  additionalQuickActions?: React.ReactNode;
  onDuplicatePostClick: () => void;
  onClose?: () => void;
};

const BUTTON_PROPS: ButtonProps & IconButtonProps = {
  variant: 'secondary',
  size: 'small',
};

const QuickActions: FC<Props> = ({ data, onDuplicatePostClick, onClose }) => {
  const { postActions } = useActions('post-detail');
  const { t } = useTranslation();

  return (
    <div
      className="tw-mr-6 tw-flex tw-items-center tw-gap-x-3"
      data-name="posts-preview_quick_actions"
      data-cy="posts-preview_quick_actions"
    >
      <Tooltip content={resolveLabel(postActions.copyLink.label)}>
        <ActionIconButton
          action={postActions.copyLink}
          actionParams={{ id: data.id }}
          data-name="posts-preview-copy-link"
          data-cy="posts-preview-copy-link"
          className="tw-hidden lg:tw-block"
          {...BUTTON_PROPS}
        />
      </Tooltip>
      <UserPermissionGate scopes={postActions.duplicate.scopes}>
        <Tooltip content={resolveLabel(postActions.duplicate.label)}>
          <ActionIconButton
            action={postActions.duplicate}
            actionParams={{ id: data.id }}
            data-name="posts-preview-duplicate"
            data-cy="posts-preview-duplicate"
            className="tw-hidden lg:tw-block"
            onClick={onDuplicatePostClick}
            {...BUTTON_PROPS}
          />
        </Tooltip>
      </UserPermissionGate>
      {!isLocalPostLocked(data) && (
        <UserPermissionGate scopes={postActions.delete.scopes}>
          <Tooltip content={resolveLabel(postActions.delete.label)}>
            <ActionIconButton
              action={postActions.delete}
              actionParams={{ id: data.id }}
              data-name="posts-preview-delete"
              data-cy="post-preview-delete"
              onClick={async () => {
                const deleted = await postActions.delete.perform({
                  id: data.id,
                });

                if (deleted) {
                  onClose?.();
                }
              }}
              {...BUTTON_PROPS}
            />
          </Tooltip>
        </UserPermissionGate>
      )}
      <QuickActionsMoreDropdown postId={data.id} />
      <UserPermissionGate scopes={postActions.edit.scopes}>
        <Tooltip
          interactive
          content={
            data.scheduling_information?.chosen_method === 'export' ? (
              <>
                {t('postAlreadyExportedOnFacebook')}
                <Link
                  to="https://intercom.help/kontentino/en/articles/6243643-how-to-edit-scheduled-posts-in-kontentino"
                  target="_blank"
                  rel="noopener"
                >
                  <div
                    data-name="edit-scheduled-post-learn-more"
                    className="tw-pl-1 tw-font-bold tw-text-white tw-underline"
                  >
                    {t('learnMore')}
                  </div>
                </Link>
              </>
            ) : undefined
          }
        >
          <div className="tw-relative">
            {!isLocalPostLocked(data) && (
              <ActionButton
                action={postActions.edit}
                actionParams={{ id: data.id }}
                data-name="posts-preview-edit"
                data-cy="post-detail-edit-button"
                size="small"
                className="tw-ml-3"
                variant="success"
              >
                {t('editPost')}
              </ActionButton>
            )}
            {data.scheduling_information?.chosen_method === 'export' && (
              <Icon
                icon={faCircleExclamation}
                className="tw-absolute -tw-right-[10px] -tw-top-[10px] tw-rounded-[50%] tw-border-[2px] tw-border-solid tw-border-grayscale-5 tw-bg-grayscale-5 tw-text-danger-100"
              />
            )}
          </div>
        </Tooltip>
      </UserPermissionGate>
    </div>
  );
};

export default QuickActions;
